import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  OutlinedInput,
} from "@mui/material";
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.js';
import StandardFormDialog from '../../components/StandardFormDialog.jsx';

export default function CadastroModulo() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [openForm, setOpenForm] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [formData, setFormData] = useState({
    st_nome: "",
    st_descricao: "",
    st_icone: "",

    bo_ativo: true,

    st_perfisPermitidos: ["todos"], // Array de perfis permitidos
  });

  const [formErrors, setFormErrors] = useState({
    st_nome: false,
    st_descricao: false,
  });

  // ✅ Verificar permissões
  useEffect(() => {
    if (!user || !['superusuario', 'gerente'].includes(user.perfil)) {
      navigate('/cockpit');
    }
  }, [user, navigate]);

  // ✅ Carregar módulo se estiver editando
  useEffect(() => {
    if (id) {
      carregarModulo();
    }
  }, [id]);

  const carregarModulo = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/modulo/${id}`);
      if (response.data?.status === 'sucesso' && response.data?.dados) {
        const modulo = response.data.dados;
        // Processar perfis permitidos
        let perfisPermitidos = ["todos"];
        if (modulo.st_perfisPermitidos) {
          try {
            const perfis = JSON.parse(modulo.st_perfisPermitidos);
            if (Array.isArray(perfis) && perfis.length > 0) {
              perfisPermitidos = perfis;
            }
          } catch (e) {
            console.warn('Erro ao parsear perfis permitidos:', e);
          }
        }

        setFormData({
          st_nome: modulo.st_nome || "",
          st_descricao: modulo.st_descricao || "",
          st_icone: modulo.st_icone || "",

          bo_ativo: modulo.bo_ativo !== undefined ? modulo.bo_ativo : true,

          st_perfisPermitidos: perfisPermitidos,
        });
      }
    } catch (error) {
      console.error('Erro ao carregar módulo:', error);
      setError('Erro ao carregar módulo');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Limpar erro do campo quando o usuário começar a digitar
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: false
      }));
    }
  };

  const fecharFormulario = () => {
    setOpenForm(false);
    navigate('/modulos');
  };

  const validarFormulario = () => {
    const erros = {
      st_nome: !formData.st_nome.trim(),
      st_descricao: !formData.st_descricao.trim(),
    };

    setFormErrors(erros);
    return !Object.values(erros).some(erro => erro);
  };

  const salvarModulo = async () => {
    if (!validarFormulario()) {
      setError("Preencha todos os campos obrigatórios.");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const dadosEnvio = {
        st_nome: formData.st_nome.trim(),
        st_descricao: formData.st_descricao.trim(),
        st_icone: formData.st_icone.trim() || null,

        bo_ativo: formData.bo_ativo,
        st_perfisPermitidos: formData.st_perfisPermitidos || ["todos"], // Array de perfis permitidos
      };

      if (id) {
        await api.put(`/modulo/${id}`, dadosEnvio);
        setSuccess("Módulo atualizado com sucesso!");
      } else {
        await api.post('/modulo', dadosEnvio);
        setSuccess("Módulo cadastrado com sucesso!");
        setFormData({
          st_nome: "",
          st_descricao: "",
          st_icone: "",

          bo_ativo: true,

          st_perfisPermitidos: ["todos"],
        });
      }

      setTimeout(() => {
        fecharFormulario();
      }, 1500);
    } catch (error) {
      console.error('Erro ao salvar módulo:', error);
      const mensagemErro = error.response?.data?.mensagem ||
        error.response?.data?.error ||
        error.message ||
        "Erro ao salvar módulo. Tente novamente.";
      setError(mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  if (!user || !['superusuario', 'gerente'].includes(user.perfil)) {
    return null;
  }

  return (
    <StandardFormDialog
      open={openForm}
      onClose={fecharFormulario}
      title={id ? "Editar Módulo" : "Novo Módulo"}
      tabs={[
        {
          label: "Informações Básicas",
          content: (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                fullWidth
                label="Nome Técnico *"
                name="st_nome"
                value={formData.st_nome}
                onChange={handleChange}
                required
                disabled={loading}
                error={formErrors.st_nome}
                helperText={formErrors.st_nome ? "Campo obrigatório" : "Nome técnico usado para importação (ex: gestao_financeira)"}
              />
              <TextField
                fullWidth
                label="Descrição *"
                name="st_descricao"
                value={formData.st_descricao}
                onChange={handleChange}
                required
                disabled={loading}
                error={formErrors.st_descricao}
                helperText={formErrors.st_descricao ? "Campo obrigatório" : "Descrição exibida no menu (ex: Gestão Financeira)"}
              />
              <TextField
                fullWidth
                label="Ícone"
                name="st_icone"
                value={formData.st_icone}
                onChange={handleChange}
                disabled={loading}
                helperText="Nome do ícone Material-UI (ex: AccountBalance, ShoppingCart)"
              />
            </Box>
          )
        },
        {
          label: "Configurações",
          content: (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.bo_ativo}
                    onChange={handleChange}
                    name="bo_ativo"
                    disabled={loading}
                  />
                }
                label="Módulo Ativo"
              />

              <FormControl fullWidth>
                <InputLabel>Perfis Permitidos *</InputLabel>
                <Select
                  multiple
                  value={formData.st_perfisPermitidos}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData(prev => ({
                      ...prev,
                      st_perfisPermitidos: typeof value === 'string' ? value.split(',') : value
                    }));
                  }}
                  input={<OutlinedInput label="Perfis Permitidos *" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value === 'todos' ? 'Todos' : value.charAt(0).toUpperCase() + value.slice(1)} size="small" />
                      ))}
                    </Box>
                  )}
                  disabled={loading}
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="superusuario">Superusuário</MenuItem>
                  <MenuItem value="gerente">Gerente</MenuItem>
                  <MenuItem value="administrador">Administrador</MenuItem>
                  <MenuItem value="operador">Operador</MenuItem>
                  <MenuItem value="beneficiario">Beneficiário</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )
        }
      ]}
      onSave={salvarModulo}
      loading={loading}
      error={error}
      success={success}
      onErrorClose={() => setError(null)}
      onSuccessClose={() => setSuccess(null)}
    />
  );
}
